/**
 * GroupInfoPanel: "Info gruppo" dei numeri Standard (WAHA).
 * Nome, descrizione, foto, partecipanti con ruolo e nome nel CRM; per gli amministratori
 * modifica di nome e descrizione, aggiunta/rimozione partecipanti, amministratori,
 * link d'invito. Sempre: esci dal gruppo. Sui numeri Ufficiale non si mostra.
 */

import { useState } from 'react';
import { X, Users, Info, UserPlus, LogOut, Loader2 } from 'lucide-react';
import { useApp } from '@/components/AppProvider';
import { GroupMemberItem, type GroupParticipantView } from './GroupMemberItem';
import { GroupEditableField } from './GroupEditableField';
import { GroupInviteSection } from './GroupInviteSection';
import { ParticipantPicker, type PickedPerson } from './ParticipantPicker';
import { ChatAvatar } from '../chat/ChatAvatar';
import { getSenderColor } from '@/lib/utils/whatsappHelpers';
import { tip, iconTip } from '@/components/messages/ChatTooltip';
import { adminHint } from './groupLogic';
import { useGroupInfo } from './useGroupInfo';

interface GroupInfoPanelProps {
  groupId: string;
  groupName: string;
  isOpen: boolean;
  onClose: () => void;
}

const border = 'border-b border-[rgba(55,53,47,0.09)] dark:border-white/10';

export function GroupInfoPanel({ groupId, groupName, isOpen, onClose }: GroupInfoPanelProps) {
  const { confirm } = useApp();
  const g = useGroupInfo(groupId, groupName, isOpen);
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<PickedPerson[]>([]);

  if (!isOpen || !g.enabled) return null;

  const canManage = g.myRole !== 'member';
  const hint = adminHint(g.myRole);
  const busy = (k: string) => g.busyKey === k;

  const onRemove = async (p: GroupParticipantView) => {
    const ok = await confirm({
      title: 'Rimuovere dal gruppo?',
      message: `${p.displayName} non riceverà più i messaggi del gruppo "${g.subject}".`,
      confirmText: 'Rimuovi', cancelText: 'Annulla', confirmVariant: 'danger',
    });
    if (ok) await g.actions.remove(p);
  };

  const onLeave = async () => {
    const ok = await confirm({
      title: 'Uscire dal gruppo?',
      message: `Il numero WhatsApp collegato uscirà dal gruppo "${g.subject}" e non riceverà più i suoi messaggi. Per rientrare servirà un nuovo invito.`,
      confirmText: 'Esci dal gruppo', cancelText: 'Annulla', confirmVariant: 'danger',
    });
    if (ok && (await g.actions.leave())) onClose();
  };

  const onAdd = async () => {
    if (picked.length === 0) return;
    if (await g.actions.add(picked.map((p) => p.digits))) {
      setPicked([]);
      setAdding(false);
    }
  };

  return (
    <div className="w-80 h-full bg-white dark:bg-gray-900 border-l border-[rgba(55,53,47,0.09)] dark:border-white/10 flex flex-col overflow-hidden">
      <div className={`flex items-center gap-3 px-4 py-3 bg-[#F7F7F5] dark:bg-gray-900 ${border}`}>
        <button onClick={onClose} className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors" {...iconTip('Chiudi info gruppo')}>
          <X className="w-5 h-5 text-[#37352F] dark:text-white" />
        </button>
        <h3 className="font-semibold text-[#37352F] dark:text-white">Info gruppo</h3>
      </div>

      {g.loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2383E2] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : g.error ? (
        <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
          <Info className="w-10 h-10 text-red-400 mb-2" />
          <p className="text-sm text-red-500">{g.error}</p>
          <button onClick={g.reload} className="mt-3 text-sm text-[#2383E2] hover:underline">Riprova</button>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className={`p-6 text-center ${border}`}>
            <div className="mx-auto mb-3 flex justify-center">
              {g.pictureUrl ? (
                <img src={g.pictureUrl} alt="" className="w-16 h-16 rounded-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <ChatAvatar name={g.subject} isGroup={true} size="lg" />
              )}
            </div>
            <GroupEditableField heading label="Nome del gruppo" value={g.subject} placeholder="Gruppo senza nome"
              canEdit={canManage} disabledReason={hint} maxLength={100} busy={busy('subject')} onSave={g.actions.rename} />
            <p className="text-sm text-[#787774] mt-1">Gruppo · {g.views.length} partecipanti</p>
          </div>

          {hint && (
            <p className={`mx-4 my-3 flex gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300`}>
              <Info className="w-4 h-4 flex-shrink-0" />{hint}
            </p>
          )}

          <div className={`p-4 ${border}`}>
            <GroupEditableField multiline label="Descrizione" value={g.description} placeholder="Nessuna descrizione"
              canEdit={canManage} disabledReason={hint} maxLength={512} busy={busy('description')} onSave={g.actions.describe} />
          </div>

          <GroupInviteSection canManage={canManage} disabledReason={hint} busy={busy('invite')}
            getInvite={g.actions.getInvite} revokeInvite={g.actions.revokeInvite} />

          <div className={`sticky top-0 z-10 flex items-center gap-2 px-4 py-2 bg-[#F7F7F5] dark:bg-gray-800 ${border}`}>
            <Users className="w-4 h-4 text-[#787774]" />
            <span className="flex-1 text-xs text-[#787774] font-medium uppercase tracking-wide">{g.views.length} partecipanti</span>
            <button type="button" onClick={() => setAdding((v) => !v)} disabled={!canManage}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md text-[#2383E2] hover:bg-[rgba(35,131,226,0.08)] disabled:opacity-40 disabled:cursor-not-allowed"
              {...tip(canManage ? 'Aggiungi Lead, Clienti o numeri al gruppo' : hint)}>
              <UserPlus className="w-3.5 h-3.5" />Aggiungi
            </button>
          </div>

          {adding && canManage && (
            <div className={`p-4 space-y-2 bg-[rgba(35,131,226,0.03)] ${border}`}>
              <ParticipantPicker selected={picked} onChange={setPicked} excludeDigits={g.existingDigits} disabled={busy('add')} />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => { setAdding(false); setPicked([]); }} className="px-2.5 py-1 text-xs rounded-md hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 text-[#37352F] dark:text-white">Annulla</button>
                <button type="button" onClick={() => void onAdd()} disabled={picked.length === 0 || busy('add')}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-[#2383E2] text-white disabled:opacity-50">
                  {busy('add') && <Loader2 className="w-3 h-3 animate-spin" />}Aggiungi {picked.length > 0 ? `(${picked.length})` : ''}
                </button>
              </div>
            </div>
          )}

          {g.views.map((p) => (
            <GroupMemberItem key={p.id} participant={p} color={getSenderColor(p.id)} canManage={canManage}
              busy={busy(`m:${p.id}`)} onRemove={(x) => void onRemove(x)} onToggleAdmin={(x) => void g.actions.toggleAdmin(x)} />
          ))}

          <div className="p-4">
            <button type="button" onClick={() => void onLeave()} disabled={busy('leave')}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50">
              {busy('leave') ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}Esci dal gruppo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
